import type { ActionStripAsset } from './registry';
import type { ActionStripDecoder } from './runtime';

export interface ActionStripImage {
  src: string;
  readonly naturalWidth: number;
  readonly naturalHeight: number;
  decode(): Promise<void>;
}

export type ActionStripImageFactory<TImage extends ActionStripImage = HTMLImageElement> = () => TImage;
export type ActionStripImageDecode<TImage extends ActionStripImage = HTMLImageElement> = (
  image: TImage,
  asset: ActionStripAsset,
  signal: AbortSignal,
) => Promise<void>;

export interface BrowserActionStripDecoderOptions<TImage extends ActionStripImage = HTMLImageElement> {
  readonly createImage?: ActionStripImageFactory<TImage>;
  readonly decode?: ActionStripImageDecode<TImage>;
}

function abortError(): DOMException {
  return new DOMException('Action strip decode aborted.', 'AbortError');
}

/** Creates the browser decoder while preserving image creation/decode seams for tests. */
export function createBrowserActionStripDecoder<TImage extends ActionStripImage = HTMLImageElement>(
  options: BrowserActionStripDecoderOptions<TImage> = {},
): ActionStripDecoder<TImage> {
  const createImage = options.createImage ?? (() => new Image() as unknown as TImage);
  const decode = options.decode ?? ((image: TImage) => image.decode());

  return async (asset, signal) => {
    if (signal.aborted) throw abortError();

    const image = createImage();
    image.src = asset.url;
    let rejectAbort!: (reason?: unknown) => void;
    const abortPromise = new Promise<never>((_resolve, reject) => {
      rejectAbort = reject;
    });
    const onAbort = (): void => rejectAbort(abortError());
    signal.addEventListener('abort', onAbort, { once: true });
    try {
      await Promise.race([
        decode(image, asset, signal),
        abortPromise,
      ]);
    } catch (error) {
      image.src = '';
      throw error;
    } finally {
      signal.removeEventListener('abort', onAbort);
    }

    if (signal.aborted) {
      image.src = '';
      throw abortError();
    }
    if (image.naturalWidth !== asset.width || image.naturalHeight !== asset.height) {
      image.src = '';
      throw new Error(
        `Action strip ${asset.key} decoded at ${image.naturalWidth}x${image.naturalHeight}; expected ${asset.width}x${asset.height}.`,
      );
    }
    return image;
  };
}

export function releaseBrowserActionStripImage(image: ActionStripImage): void {
  image.src = '';
}
